import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

interface AuthUser {
  email: string
  name: string
  picture?: string
}

interface AuthContextValue {
  user: AuthUser | null
  loading: boolean
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  async function fetchUser() {
    const r = await fetch('/api/me')
    if (r.status === 401) {
      navigate('/login')
      return
    }
    const u = await r.json() as AuthUser
    setUser(u)
  }

  useEffect(() => {
    fetchUser().catch(() => navigate('/login')).finally(() => setLoading(false))
  }, [navigate])

  return (
    <AuthContext.Provider value={{ user, loading, refreshUser: fetchUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
